/**
 * Tenant-scoped cart persistence and fail-closed isolation rules.
 *
 * Persistence identity is restaurant ID only (never slug/hostname):
 *   gmf-cart-v1:{restaurantId}
 * Legacy unscoped `gmf-cart-v1` is migrated after tenant resolution — never
 * blindly hydrated into the current tenant.
 */

import type { CartLine, CartStoreSnapshot } from "@/shared/cart/model.types";

/** Legacy origin-global persist key (pre–tenant isolation). */
export const LEGACY_CART_PERSIST_KEY = "gmf-cart-v1";

/** Zustand persist version — state shape is unchanged; tenancy is the storage key. */
export const CART_PERSIST_VERSION = 3;

export type CartLineTenantClassification =
  | { kind: "empty" }
  | { kind: "safe"; restaurantId: string }
  | { kind: "foreign"; restaurantId: string }
  | { kind: "mixed" }
  | { kind: "missing_id" };

export class CartTenantIsolationError extends Error {
  constructor(message = "Your cart isn't available for this restaurant.") {
    super(message);
    this.name = "CartTenantIsolationError";
  }
}

export function normalizeCartTenantId(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

export function cartPersistKey(restaurantId: string): string {
  const rid = normalizeCartTenantId(restaurantId);
  if (!rid) {
    throw new CartTenantIsolationError("Cart persistence requires a restaurant id.");
  }
  return `${LEGACY_CART_PERSIST_KEY}:${rid}`;
}

export function isTenantScopedCartPersistKey(key: string | null | undefined): boolean {
  const k = String(key ?? "");
  return k.startsWith(`${LEGACY_CART_PERSIST_KEY}:`) && k !== LEGACY_CART_PERSIST_KEY;
}

export function lineRestaurantId(line: Pick<CartLine, "restaurant_id"> | null | undefined): string {
  return normalizeCartTenantId(line?.restaurant_id);
}

export function classifyCartLines(
  lines: ReadonlyArray<Pick<CartLine, "restaurant_id">> | null | undefined,
  currentRestaurantId?: string | null
): CartLineTenantClassification {
  const items = Array.isArray(lines) ? lines : [];
  if (items.length === 0) return { kind: "empty" };

  const ids = new Set<string>();
  for (const line of items) {
    const rid = lineRestaurantId(line);
    if (!rid) return { kind: "missing_id" };
    ids.add(rid);
  }

  if (ids.size > 1) return { kind: "mixed" };

  const only = [...ids][0];
  const current = normalizeCartTenantId(currentRestaurantId);
  if (current && only !== current) return { kind: "foreign", restaurantId: only };
  return { kind: "safe", restaurantId: only };
}

/** True only when every line belongs to `restaurantId`. Empty cart is safe. */
export function areCartLinesSafeForTenant(
  lines: ReadonlyArray<Pick<CartLine, "restaurant_id">> | null | undefined,
  restaurantId: string
): boolean {
  const rid = normalizeCartTenantId(restaurantId);
  if (!rid) return false;
  const classification = classifyCartLines(lines, rid);
  return classification.kind === "empty" || classification.kind === "safe";
}

export function assertCartBelongsToTenant(
  lines: ReadonlyArray<Pick<CartLine, "restaurant_id">> | null | undefined,
  restaurantId: string
): void {
  const rid = normalizeCartTenantId(restaurantId);
  if (!rid) {
    throw new CartTenantIsolationError();
  }
  if (!areCartLinesSafeForTenant(lines, rid)) {
    throw new CartTenantIsolationError();
  }
}

/**
 * Checkout / preview / payment-start invariant.
 * When a tenant cart is isolated, the resolved restaurant id must match that
 * isolation scope — in addition to every line belonging to it.
 */
export function assertCheckoutCartInvariant(
  lines: ReadonlyArray<Pick<CartLine, "restaurant_id">> | null | undefined,
  restaurantId: string
): void {
  const rid = normalizeCartTenantId(restaurantId);
  if (!rid) {
    throw new CartTenantIsolationError();
  }
  const isolated = getIsolatedRestaurantId();
  if (isolated && isolated !== rid) {
    throw new CartTenantIsolationError();
  }
  assertCartBelongsToTenant(lines, rid);
}

const EMPTY_SAFE_CART_ITEMS: never[] = []

export function selectSafeCartItems<T extends Pick<CartLine, "restaurant_id">>(
  lines: ReadonlyArray<T>,
  restaurantId: string | null | undefined
): T[] {
  const rid = normalizeCartTenantId(restaurantId);
  if (!rid) return EMPTY_SAFE_CART_ITEMS as T[];
  if (!areCartLinesSafeForTenant(lines, rid)) return EMPTY_SAFE_CART_ITEMS as T[];
  return Array.isArray(lines) ? (lines as T[]) : (EMPTY_SAFE_CART_ITEMS as T[]);
}

export type PersistedCartBlob = {
  state?: Partial<CartStoreSnapshot> | null;
  version?: number;
};

export function parsePersistedCartBlob(raw: string | null | undefined): CartStoreSnapshot | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as PersistedCartBlob;
    const state = parsed?.state;
    if (state == null || typeof state !== "object") return null;
    return {
      items: Array.isArray(state.items) ? state.items : [],
      isLocked: Boolean(state.isLocked),
      isCheckoutLocked: Boolean(state.isCheckoutLocked),
    };
  } catch {
    return null;
  }
}

export function serializePersistedCartBlob(
  snapshot: CartStoreSnapshot,
  version: number = CART_PERSIST_VERSION
): string {
  return JSON.stringify({
    state: {
      items: snapshot.items,
      isLocked: snapshot.isLocked,
      isCheckoutLocked: snapshot.isCheckoutLocked,
    },
    version,
  });
}

function readSnapshot(storage: Storage, key: string): CartStoreSnapshot | null {
  try {
    return parsePersistedCartBlob(storage.getItem(key));
  } catch {
    return null;
  }
}

function writeSnapshot(storage: Storage, key: string, snapshot: CartStoreSnapshot): void {
  storage.setItem(key, serializePersistedCartBlob(snapshot));
}

function snapshotIsEmpty(snapshot: CartStoreSnapshot | null): boolean {
  return !snapshot || !Array.isArray(snapshot.items) || snapshot.items.length === 0;
}

/**
 * Migrate unscoped `gmf-cart-v1` after tenant resolution.
 * Never hydrates a foreign/mixed/missing-id blob into the current tenant key.
 */
export function migrateLegacyCartBlob(storage: Storage, currentRestaurantId: string): void {
  const currentRid = normalizeCartTenantId(currentRestaurantId);
  if (!currentRid) return;

  let legacy: CartStoreSnapshot | null;
  try {
    if (storage.getItem(LEGACY_CART_PERSIST_KEY) == null) return;
    legacy = readSnapshot(storage, LEGACY_CART_PERSIST_KEY);
  } catch {
    return;
  }

  const classification = classifyCartLines(legacy?.items, currentRid);

  if (classification.kind === "empty" || !legacy) {
    try {
      storage.removeItem(LEGACY_CART_PERSIST_KEY);
    } catch {
      /* private mode */
    }
    return;
  }

  if (classification.kind === "safe") {
    const destKey = cartPersistKey(currentRid);
    const existing = readSnapshot(storage, destKey);
    if (snapshotIsEmpty(existing)) {
      writeSnapshot(storage, destKey, legacy);
    }
    try {
      storage.removeItem(LEGACY_CART_PERSIST_KEY);
    } catch {
      /* private mode */
    }
    return;
  }

  if (classification.kind === "foreign") {
    const destKey = cartPersistKey(classification.restaurantId);
    const existing = readSnapshot(storage, destKey);
    if (snapshotIsEmpty(existing)) {
      writeSnapshot(storage, destKey, legacy);
    }
    try {
      storage.removeItem(LEGACY_CART_PERSIST_KEY);
    } catch {
      /* private mode */
    }
  }

  // mixed / missing_id: leave the legacy blob unhydrated (fail closed).
}

let persistWritesEnabled = false;
let isolatedRestaurantId: string | null = null;
let activePersistKey: string | null = null;
let isolationStatus: "pending" | "ready" = "pending";
const persistKeyListeners = new Set<(key: string | null) => void>();

export function isCartPersistWriteEnabled(): boolean {
  return persistWritesEnabled;
}

export function getIsolatedRestaurantId(): string | null {
  return isolatedRestaurantId;
}

export function getActiveCartPersistKey(): string | null {
  return activePersistKey;
}

export function getCartIsolationStatus(): "pending" | "ready" {
  return isolationStatus;
}

export function subscribeActiveCartPersistKey(listener: (key: string | null) => void): () => void {
  persistKeyListeners.add(listener);
  return () => {
    persistKeyListeners.delete(listener);
  };
}

function emitPersistKey(key: string | null): void {
  activePersistKey = key;
  for (const listener of persistKeyListeners) {
    listener(key);
  }
}

export function beginCartIsolationPending(): void {
  persistWritesEnabled = false;
  isolatedRestaurantId = null;
  isolationStatus = "pending";
  emitPersistKey(null);
}

export function completeCartIsolation(restaurantId: string, persistKey: string): void {
  persistWritesEnabled = true;
  isolatedRestaurantId = normalizeCartTenantId(restaurantId) || null;
  isolationStatus = isolatedRestaurantId ? "ready" : "pending";
  emitPersistKey(isolatedRestaurantId ? persistKey : null);
}

export function resetCartTenantIsolationForTests(): void {
  persistWritesEnabled = false;
  isolatedRestaurantId = null;
  isolationStatus = "pending";
  emitPersistKey(null);
}
